import { useSelector } from "react-redux";

const ADMIN_ROLES = new Set(["admin", "administrator", "super_admin", "system_admin"]);
const USER_ROLES = new Set(["user"]);

const getRoleValue = (role) => {
  if (role && typeof role === "object") {
    return role.name || role.role || role.value || role.label || "";
  }
  return role;
};

const normalizeRoleName = (role) =>
  String(getRoleValue(role)).trim().toLowerCase().replace(/[\s-]+/g, "_");

// Named per-user AI/admin feature gates (2026-08-05). `roles` is a
// Salesforce-synced tier shared by several admins; these capabilities are
// granted to specific accounts by email on the backend
// (`users/capabilities.py` in dms_backend) and arrive here as a plain
// array on the `/auth/me/` response — see AuthSlice's `user.capabilities`.
// Treated as empty (nothing granted) until the user object loads so a
// component can render its "no access" state instead of flashing a
// restricted surface during the initial fetch.
const EMPTY_CAPABILITIES = Object.freeze(new Set());

const useAuth = () => {
  // Optional chaining on `state.auth`: several component tests mock
  // `react-redux`'s `useSelector` with a partial state tree that has
  // no `auth` key at all (they were written before any component in
  // their tree called `useAuth`). A direct `state.auth.user` throws
  // in that case; `state.auth?.user` degrades to "no user" instead.
  const user = useSelector((state) => state.auth?.user);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  const role = user?.roles !== undefined ? user.roles : (user?.role ? [user.role] : undefined);
  const hasRole = role !== undefined && role !== null;
  const roles = Array.isArray(role) ? role : hasRole ? [role] : [];
  const hasAdminRole = roles.some((item) => ADMIN_ROLES.has(normalizeRoleName(item)));
  const isAdmin = hasRole
    ? hasAdminRole || Boolean(user?.is_staff || user?.is_superuser)
    : user?.is_staff || user?.is_superuser || undefined;
  const isUser = hasRole
    ? roles.some((item) => USER_ROLES.has(normalizeRoleName(item)))
    : undefined;

  const capabilities = Array.isArray(user?.capabilities)
    ? new Set(user.capabilities)
    : EMPTY_CAPABILITIES;
  const hasCapability = (name) => capabilities.has(name);

  return {
    user,
    isAuthenticated,
    role,
    isAdmin,
    isUser,
    capabilities,
    hasCapability
  };
};

export default useAuth;
